// ./src/components/features/EventPartners/PartnerPackageSelect/PartnerPackageSelect.tsx
//
// Self-fetching Select over the account's reusable partner packages. Reports the
// full package on change so the drawer can prefill the agreed amount / currency /
// money direction. Mirrors VenueSelect (fetch once, no provider).

import React, { useEffect, useState } from "react";
import { SelectProps } from "@mantine/core";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { PartnerPackage, PartnerPackageService, listAllPages } from "@/utils/api";
import { SelectField } from "@/components/generic";

export interface PartnerPackageSelectProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  value: string | null;
  onChange: (id: string | null, pkg: PartnerPackage | null) => void;
  /** Keeps a preselected value readable before the list loads. */
  fallbackLabel?: string | null;
  /**
   * The event this picker is shown on — passed as an authorization context so a cross-account crew
   * member (co_host with event.operate) sees the event account's packages instead of an empty list.
   */
  eventId?: string | null;
  /**
   * Packages to leave OUT of the list — used by the offerings page, where an event may offer each
   * package only once (the table carries UNIQUE (event_id, partner_package_id, removed_at_str)).
   *
   * ⚠ The CURRENT value is never excluded, whatever this contains. Hiding the selected package
   * would blank the field the moment a drawer opened on an existing row.
   */
  excludeIds?: string[];
}

export const PartnerPackageSelect: React.FC<PartnerPackageSelectProps> = ({
  value,
  onChange,
  fallbackLabel,
  eventId,
  excludeIds,
  ...rest
}) => {
  const [packages, setPackages] = useState<PartnerPackage[]>([]);

  useEffect(() => {
    let cancelled = false;

    listAllPages(PartnerPackageService.list, { ...(eventId ? { eventId } : {}) }).then((result) => {
      if (cancelled) {
        return;
      }

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        setPackages(result.data as PartnerPackage[]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const excluded = new Set((excludeIds || []).filter((id) => id !== value));
  const data = packages
    .filter((p) => !excluded.has(p.id as string))
    .map((p) => ({ value: p.id as string, label: p.name || "—" }));

  if (value && !data.some((d) => d.value === value)) {
    data.unshift({ value, label: fallbackLabel || "…" });
  }

  return (
    <SelectField
      data={data}
      value={value}
      onChange={(_name, id) => onChange(id, packages.find((p) => p.id === id) || null)}
      searchable
      clearable
      {...rest}
    />
  );
};
